/**
 * File: CheckList.tsx
 * Path: apps/admin/src/features/diagnostics/components/CheckList.tsx
 * Description: A list of diagnostic checks: a mark, what was checked, what
 * was found and, when something is wrong, what to do.
 */

import React from "react";
import { AlertTriangle, CheckCircle2, Info, XCircle } from "lucide-react";
import type { CheckStatus, DiagnosticCheck } from "@idk/config";
import { cn } from "@idk/utils";

const MARK: Record<CheckStatus, { icon: typeof Info; className: string; label: string }> = {
  ok: { icon: CheckCircle2, className: "text-tone-green", label: "Working" },
  warn: { icon: AlertTriangle, className: "text-tone-amber", label: "Warning" },
  fail: { icon: XCircle, className: "text-destructive", label: "Problem" },
  info: { icon: Info, className: "text-muted-foreground", label: "Note" },
};

export const StatusMark: React.FC<{ status: CheckStatus; className?: string }> = ({ status, className }) => {
  const { icon: Icon, className: tone, label } = MARK[status];
  return <Icon className={cn("h-4 w-4 shrink-0", tone, className)} aria-label={label} role="img" />;
};

export const CheckList: React.FC<{ checks: readonly DiagnosticCheck[] }> = ({ checks }) => (
  <ul>
    {checks.map((check) => (
      <li
        key={check.id}
        className="flex items-start gap-stack-sm border-b border-border/50 py-stack-sm last:border-b-0"
        data-status={check.status}
      >
        <StatusMark status={check.status} className="translate-y-px" />
        <div className="min-w-0 space-y-stack-xs">
          <p className="text-sm font-medium">{check.label}</p>
          <p className="text-sm text-muted-foreground">{check.detail}</p>
          {check.fix && <p className="text-sm">Fix: {check.fix}</p>}
        </div>
      </li>
    ))}
  </ul>
);
